import React, { useState, useCallback, useEffect, useMemo } from 'react';
// @mui
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Container from '@mui/material/Container';  
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import FormControl from '@mui/material/FormControl'; 
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';  
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress'; 
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// components (place before local hooks)
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';  
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useSnackbar } from 'src/components/snackbar'; // <--- 1. Import Snackbar

// types
import { VuonTrong } from 'src/types/vuontrong'; 
import { LoSam } from 'src/types/losam';
import { CaySam } from 'src/types/caysam';
// local hooks & sections
import { losamApiService } from 'src/api/vuontrong-api';
import { LoSamCamera } from 'src/types/camera';
import { OptionModel } from 'src/types/api-response';
import useVuonTrong from '../../vuontrong/hooks/use-vuontrong';
import { useLoSam } from '../hooks/use-losam';
import useTraCuuLoSam, { LoSamPosition } from '../hooks/use-tracu';
import useGetCaySam from '../hooks/use-get-caysam'; // <--- 2. Import Hook mới

import CaySamDrawer from '../cay-sam/cay-sam-edit-drawer';
import LotGrid from '../components/lot-grid';
import UpdateCaySams from '../cay-sam/update-cay-sams';
import FilterCaySam from '../components/filter-cay-sam';
import CameraViewer from '../components/camera-viewer';
import { InMaQRPopup } from '../components/in-ma-qr-popup';
import { ChuthichSection } from '../components/chu-thich';

// ----------------------------------------------------------------------

export default function TraCuuLoSamView() {
  const settings = useSettingsContext(); 
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar(); // <--- 3. Khởi tạo Snackbar
 
  const [selectedGarden, setSelectedGarden] = useState<OptionModel | null>(null); 
  const [selectedLot, setSelectedLot] = useState<LoSam | null>(null); 
  const [gridSize, setGridSize] = useState({ x: 0, y: 0 });
  const [gridPositions, setGridPositions] = useState<LoSamPosition[][]>([]);
  const [selectedPositions, setSelectedPositions] = useState<LoSamPosition[]>([]);
  const [lotName, setLotName] = useState('');
  const [loading, setLoading] = useState(false);

  // moved to hook
  const [lots, setLots] = useState<LoSam[]>([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [caySams, setCaySams] = useState<CaySam[]>([]);
  const [caySamsLoading, setCaySamsLoading] = useState(false);
  const [lotDetailLoading, setLotDetailLoading] = useState(false);
  const [lotDetailLoaded, setLotDetailLoaded] = useState(false); 

  // State for deselected ginsengs when filtering
  const [deselectedGinsengs, setDeselectedGinsengs] = useState<Set<string>>(new Set());
  
  // State for update growth modal
  const [updateGrowthOpen, setUpdateGrowthOpen] = useState(false);
  const [filteredCaySams, setFilteredCaySams] = useState<any[]>([]);

  // Thay đổi: Bỏ gardens, chỉ dùng optiongardens
  const { optiongardens, getOptionVuonTrong, loading: gardensLoading } = useVuonTrong();
  const { listLotsByVuonTrong, getLoSamById, parseViTriTrongLo, diemSucKhoeOptions } = useLoSam();

  // <--- 4. Khởi tạo Hook useGetCaySam
  const { addDinhKemFile, loadingAddFile } = useGetCaySam();
 
  // Load lots when garden is selected 
  const loadLots = useCallback(async (vuonTrongId: number) => {
    try {
      setLotsLoading(true); 
      const data = await listLotsByVuonTrong(vuonTrongId); 
      setLots(data);
    } catch (error) {
      console.error('Error loading lots:', error);
      setLots([]);
    } finally {
      setLotsLoading(false);  
    }
  }, [listLotsByVuonTrong]);

  // Load cây sâm when lot is selected
  const loadCaySams = useCallback(async (loSamId: number) => {
    try {
      setCaySamsLoading(true);
      const response = await getLoSamById(loSamId);
      const lotData = response.oneItem as LoSam | null;
      setCaySams(lotData?.caySams || []);
    } catch (error) {
      console.error('Error loading cây sâm:', error);
      setCaySams([]);
    } finally {
      setCaySamsLoading(false);
    }
  }, [getLoSamById]);

  // Reload lot detail and update grid positions
  const reloadLotDetail = useCallback(async (loSamId: number) => {
    try {
      setCaySamsLoading(true);
      const lotDetail = await losamApiService.getLoSam(loSamId);
      if (lotDetail.messCode === 1 && lotDetail.oneItem) {
        setSelectedLot(lotDetail.oneItem as LoSam);
        
        // Cập nhật gridPositions với dữ liệu mới
        const totalCols = lotDetail.oneItem.soCot + 1;
        const totalRows = lotDetail.oneItem.soHang;
        
        const positions: LoSamPosition[][] = [];
        for (let y = 0; y < totalRows; y += 1) {
          const row: LoSamPosition[] = []; 
          for (let x = 0; x < totalCols; x += 1) {
            row.push({
              x,
              y,
              selected: false,
              occupied: false,
              lotId: lotDetail.oneItem.loSam_ID || 0,
            });
          }
          positions.push(row);
        }
        setGridPositions(positions);
        
        // Load cây sâm data
        const caySamsData = lotDetail.oneItem.caySams || [];
        setCaySams(caySamsData);
        
        // Map cây sâm to grid positions
        const updatedPositions = positions.map((row) =>
          row.map((pos) => {
            const middleColumn = Math.floor(totalCols / 2);
            if (pos.x === middleColumn) {
              return pos;
            }
            
            let adjustedX = pos.x;
            if (pos.x > middleColumn) {
              adjustedX = pos.x - 1;
            }
            
            const caySam = caySamsData.find((cs: CaySam) => {
              const coords = parseViTriTrongLo(cs.viTriTrongLo);
              return coords ? coords.x === adjustedX && coords.y === pos.y : false;
            });
             
            return {
              ...pos,
              occupied: !!caySam,
              caySam: caySam || undefined,
            };
          })
        );
        setGridPositions(updatedPositions);
      }
    } catch (error) {
      console.error('Error reloading lot detail:', error);
    } finally {
      setCaySamsLoading(false);
    }
  }, [parseViTriTrongLo]);

  const handleGardenChange = useCallback((gardenId: number) => {
    const garden = optiongardens.find((g) => Number(g.value) === gardenId) || null;
    setSelectedGarden(garden);
    
    setSelectedLot(null);
    setGridPositions([]);
    setSelectedPositions([]);
    setGridSize({ x: 0, y: 0 });
    setLots([]);
    setCaySams([]);
    
    if (garden && garden.value) {
      loadLots(Number(garden.value));
    }
  }, [optiongardens, loadLots]); 

  const handleLotChange = useCallback(async (lotId: number) => {
    const lot = lots.find((l) => l.loSam_ID === lotId);
    setSelectedLot(lot || null);
    setLotDetailLoading(true);
    setLotDetailLoaded(false);
    
    // ===== RESET TẤT CẢ STATE TRƯỚC KHI LOAD MỚI =====
    setGridPositions([]);
    setCaySams([]);
    setSelectedPositions([]);
    setGridSize({ x: 0, y: 0 });
    setLotName('');
    
    if (lot) {
      try {
        // Gọi API để lấy thông tin chi tiết lô sâm (bao gồm loSamCameras)
        const lotDetail = await losamApiService.getLoSam(lotId);
        
        if (lotDetail.messCode === 1 && lotDetail.oneItem) {
          // Cập nhật selectedLot với dữ liệu chi tiết từ API
          setSelectedLot(lotDetail.oneItem as LoSam);
          
          // Add 1 to soCot for walkway
          const totalCols = lotDetail.oneItem.soCot + 1;
          const totalRows = lotDetail.oneItem.soHang;
          
          setGridSize({ x: totalCols, y: totalRows });
          setLotName(lotDetail.oneItem.tenLo);
          
          // Initialize EMPTY grid positions FIRST
          const positions: LoSamPosition[][] = [];
          for (let y = 0; y < totalRows; y += 1) {
            const row: LoSamPosition[] = []; 
            for (let x = 0; x < totalCols; x += 1) {
              row.push({
                x,
                y,
                selected: false,
                occupied: false, // Mặc định là trống
                lotId: lotDetail.oneItem.loSam_ID || 0,
              });
            }
            positions.push(row);
          }
          setGridPositions(positions);
          
          // Load cây sâm data AFTER initializing grid
          await loadCaySams(lotDetail.oneItem.loSam_ID || 0);
          
          setSelectedPositions([]);
          setLotDetailLoaded(true);
        } else {
          console.error('Error loading lot detail:', lotDetail.message);
          // Fallback to basic lot info
          setSelectedLot(lot);
          const totalCols = lot.soCot + 1;
          const totalRows = lot.soHang;
          setGridSize({ x: totalCols, y: totalRows });
          setLotName(lot.tenLo);
          
          // Initialize empty grid
          const positions: LoSamPosition[][] = [];
          for (let y = 0; y < totalRows; y += 1) {
            const row: LoSamPosition[] = [];
            for (let x = 0; x < totalCols; x += 1) {
              row.push({
                x,
                y,
                selected: false,
                occupied: false,
                lotId: lot.loSam_ID || 0,
              });
            }
            positions.push(row);
          }
          setGridPositions(positions);
          
          await loadCaySams(lot.loSam_ID || 0);
        }
      } catch (error) {
        console.error('Error loading lot detail:', error);
        // Fallback to basic lot info
        setSelectedLot(lot);
        const totalCols = lot.soCot + 1;
        const totalRows = lot.soHang;
        setGridSize({ x: totalCols, y: totalRows });
        setLotName(lot.tenLo);
        
        // Initialize empty grid
        const positions: LoSamPosition[][] = [];
        for (let y = 0; y < totalRows; y += 1) {
          const row: LoSamPosition[] = [];
          for (let x = 0; x < totalCols; x += 1) {
            row.push({
              x,
              y,
              selected: false,
              occupied: false,
              lotId: lot.loSam_ID || 0,
            });
          }
          positions.push(row);
        }
        setGridPositions(positions);
        
        await loadCaySams(lot.loSam_ID || 0);
      } finally {
        setLotDetailLoading(false);
      }
    } else {
      setGridSize({ x: 0, y: 0 });
      setLotName('');
      setGridPositions([]);
      setLotDetailLoading(false);
    }
  }, [lots, loadCaySams]);

  // Update grid positions when caySams data is loaded
  useEffect(() => {
    if (caySams.length > 0 && gridPositions.length > 0) {
      setGridPositions((prev) => {
        const newPositions = prev.map((row) =>
          row.map((pos) => {
            // Skip walkway column
            const middleColumn = Math.floor(gridSize.x / 2);
            if (pos.x === middleColumn) {
              return pos; // Keep walkway as is
            }
            
            // Adjust x coordinate for positions after walkway
            let adjustedX = pos.x;
            if (pos.x > middleColumn) {
              adjustedX = pos.x - 1; // Shift back by 1 for positions after walkway
            }
            
            // Find cây sâm at this adjusted position
            const caySam = caySams.find((cs) => {
              const coords = parseViTriTrongLo(cs.viTriTrongLo);
              return coords ? coords.x === adjustedX && coords.y === pos.y : false;
            });
             
            return {
              ...pos,
              occupied: !!caySam,
              caySam: caySam || undefined,
            };
          })
        );
        return newPositions;
      });
    }
  }, [caySams, gridPositions.length, gridSize.x, parseViTriTrongLo]);

  useEffect(()=>{
    getOptionVuonTrong();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])
 
  const tracu = useTraCuuLoSam({ selectedLot, gridSize, gridPositions, loadCaySams });

  const {handleCloseDrawer} = tracu; 

  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const handleCloseQRDialog = () => {
    setQrPopupOpen(false);
  };
  const handleOpenQRDialog = () => {
    setQrPopupOpen(true);
  };

  // Filter highlight criteria
  const [highlightStatus, setHighlightStatus] = useState<number>(0); 
  const [highlightHealth, setHighlightHealth] = useState<number>(0); 
  const [highlightAge, setHighlightAge] = useState<number>(0);
  const [filteredCount, setFilteredCount] = useState<number>(0);

  // Get ginseng data from real cây sâm
  const getGinsengInfo = useCallback((x: number, y: number) => {
    const middleColumn = Math.floor(gridSize.x / 2);
    if (x === middleColumn) return null; // Walkway
    
    const position = gridPositions[y]?.[x];
    
    if (position?.caySam) {
      const {caySam} = position;

      // Lấy nhật ký mới nhất (nếu có) để lấy điểm sức khỏe/tình trạng hiện tại
      let latestDiem: number | undefined;
      let latestTinhTrang: number | undefined;
      if (Array.isArray(caySam.caySamNhatKys) && caySam.caySamNhatKys.length > 0) {
        const list = caySam.caySamNhatKys.filter((n): n is any => !!n);
        if (list.length > 0) {
          const latest = list.reduce((acc: any, cur: any) => {
            if (!acc) return cur;
            return new Date(cur.ngayGhi).getTime() > new Date(acc.ngayGhi).getTime() ? cur : acc;
          }, null as any);
          latestDiem = latest?.diemSucKhoe;
          latestTinhTrang = latest?.tinhTrang;
        }
      }

      return {
        id: caySam.caySam_ID,
        name: `Cây sâm ${caySam.maCaySam || 'N/A'}`,
        description: `Cây sâm với mã ${caySam.maCaySam || 'N/A'}, vị trí ${caySam.viTriTrongLo}`,
        blockChain: caySam.blockChain,
        viTriTrongLo: caySam.viTriTrongLo,
        loSam_ID: caySam.loSam_ID,
        loSam: caySam.loSam,
        tuoiCay_ID: caySam.tuoiCay_ID,
        diemSucKhoe: latestDiem,
        tinhTrang: latestTinhTrang,
      };
    }
    return null;
  }, [gridPositions, gridSize.x]);

 const handleApplyFilter = useCallback((criteria: { tinhTrang?: number; diem?: number; tuoi?: number; excludeLoggedThisMonth?: boolean }) => {
    // Nếu tất cả đều không chọn (undefined hoặc 0) thì reset
    if (!criteria.tinhTrang && !criteria.diem && !criteria.tuoi) {
      setHighlightStatus(0);
      setHighlightHealth(0);
      setHighlightAge(0);
      setFilteredCount(0);
      return;
    }

    // Cập nhật trạng thái highlight (Nếu không chọn thì set về 0)
    setHighlightStatus(criteria.tinhTrang || 0);
    setHighlightHealth(criteria.diem || 0);
    setHighlightAge(criteria.tuoi || 0);

    const middleColumn = Math.floor(gridSize.x / 2);
    let count = 0;

    gridPositions.forEach((row, y) => {
      row.forEach((_, x) => {
        const positionKey = `${x}-${y}`;
        const info = (x !== middleColumn && !deselectedGinsengs.has(positionKey)) ? getGinsengInfo(x, y) : null;
        
        if (info) {
          const ii = info as any;
          // Logic lọc linh hoạt: Chỉ so sánh những tiêu chí được chọn
          const match = (
            (!criteria.tinhTrang || ii.tinhTrang === criteria.tinhTrang) &&
            (!criteria.diem || ii.diemSucKhoe === criteria.diem) &&
            (!criteria.tuoi || Number(ii.tuoiCay_ID) === Number(criteria.tuoi))
          );

          if (match) {
            count += 1;
          }
        }
      });
    });
    
    setFilteredCount(count);
  }, [gridPositions, gridSize.x, getGinsengInfo, deselectedGinsengs]); 
   
  // Handle deselecting ginsengs when filtering
  const handleDeselectGinseng = useCallback((x: number, y: number) => {
    const key = `${x}-${y}`;
    setDeselectedGinsengs((prev) => {
      const newSet = new Set(prev);
      // TOGGLE: if already deselected, select back; otherwise deselect
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }   
      
      if (!highlightStatus && !highlightHealth && !highlightAge) { 
        setFilteredCount(0);
        return newSet; 
      }

      const middleColumn = Math.floor(gridSize.x / 2);
      let count = 0;
      for (let yy = 0; yy < gridPositions.length; yy += 1) {
        const row = gridPositions[yy] || [];
        for (let xx = 0; xx < row.length; xx += 1) {
          const positionKey = `${xx}-${yy}`;
          const info = (xx !== middleColumn && !newSet.has(positionKey)) ? getGinsengInfo(xx, yy) : null;
          if (info) {
            const ii = info as any;
            
            const hasRequiredData = ( 
              (!highlightStatus || (ii.tinhTrang !== null && ii.tinhTrang !== undefined)) &&
              (!highlightHealth || (ii.diemSucKhoe !== null && ii.diemSucKhoe !== undefined)) &&
              (!highlightAge || (ii.tuoiCay_ID !== null && ii.tuoiCay_ID !== undefined))
            ); 
            
            const match = hasRequiredData && (
              (highlightStatus ? ii.tinhTrang === highlightStatus : true) &&  
              (highlightHealth ? ii.diemSucKhoe === highlightHealth : true) &&
              (highlightAge ? Number(ii.tuoiCay_ID) === Number(highlightAge) : true)
            );
            
            if (match) count += 1;  
          }
        }
      }
      setFilteredCount(count);
      return newSet;
    });
  }, [gridPositions, gridSize.x, getGinsengInfo, highlightStatus, highlightHealth, highlightAge]);

  // Handle opening update growth modal with filtered cays (excluding deselected)
  const handleUpdateGrowth = useCallback(() => {
    const middleColumn = Math.floor(gridSize.x / 2);
    const filteredCays: any[] = [];
    
    gridPositions.forEach((row, y) => {
      row.forEach((_, x) => {
        const positionKey = `${x}-${y}`;
        const info = (x !== middleColumn && !deselectedGinsengs.has(positionKey)) ? getGinsengInfo(x, y) : null;
        
        if (info) {
          const ii = info as any;

          const hasRequiredData = (
            (!highlightStatus || ii.tinhTrang != null) &&
            (!highlightHealth || ii.diemSucKhoe != null) &&
            (!highlightAge || ii.tuoiCay_ID != null)
          );

          const match = hasRequiredData && (
            (highlightStatus ? ii.tinhTrang === highlightStatus : true) &&
            (highlightHealth ? ii.diemSucKhoe === highlightHealth : true) &&
            (highlightAge ? Number(ii.tuoiCay_ID) === Number(highlightAge) : true)
          );

          if (match) {
            filteredCays.push({
              id: ii.id,
              maCaySam: ii.name,
              viTri: `${String.fromCharCode(65 + x)}${y + 1}`,
              diem: ii.diemSucKhoe || 'undefined',
              tt: ii.tinhTrang || 'undefined',
              blockChain: ii.blockChain,
            });
          }
        }
      });
    });

    setFilteredCaySams(filteredCays);
    setUpdateGrowthOpen(true);
  }, [gridPositions, gridSize.x, deselectedGinsengs, getGinsengInfo, highlightStatus, highlightHealth, highlightAge]);

  // Callback để handle refresh từ LotGrid
  const handleCaySamUpdated = useCallback(() => {
    if (selectedLot?.loSam_ID) {
      reloadLotDetail(selectedLot.loSam_ID);
    }
  }, [selectedLot, reloadLotDetail]);

  // <--- 5. THÊM LOGIC XỬ LÝ IMPORT PDF
  const handleImportFilePdf = useCallback(async (file: File) => {
    // 5.1. Lấy danh sách ID cây sâm thỏa mãn bộ lọc hiện tại
    const middleColumn = Math.floor(gridSize.x / 2);
    const targetIds: string[] = [];

    gridPositions.forEach((row, y) => {
      row.forEach((_, x) => {
        const positionKey = `${x}-${y}`;
        // Bỏ qua đường đi và các cây bị người dùng bỏ chọn thủ công
        if (x !== middleColumn && !deselectedGinsengs.has(positionKey)) {
          const info = getGinsengInfo(x, y);
          
          if (info) {
            const ii = info as any;
            // Kiểm tra cây này có khớp với bộ lọc (Filter) hiện tại không
            const hasRequiredData = (
              (!highlightStatus || ii.tinhTrang != null) &&
              (!highlightHealth || ii.diemSucKhoe != null) &&
              (!highlightAge || ii.tuoiCay_ID != null)
            );

            const match = hasRequiredData && (
              (highlightStatus ? ii.tinhTrang === highlightStatus : true) &&
              (highlightHealth ? ii.diemSucKhoe === highlightHealth : true) &&
              (highlightAge ? Number(ii.tuoiCay_ID) === Number(highlightAge) : true)
            );

            if (match) {
              // Convert ID sang string
              targetIds.push(String(ii.id));
            }
          }
        }
      });
    });

    // 5.2. Kiểm tra nếu không có cây nào
    if (targetIds.length === 0) {
      enqueueSnackbar('Không tìm thấy cây sâm nào phù hợp với bộ lọc để đính kèm file!', { variant: 'warning' });
      return;
    }

    // 5.3. Gọi API thông qua Hook
    const success = await addDinhKemFile(targetIds, file);

    if (success) {
      enqueueSnackbar(`Đã đính kèm file PDF cho ${targetIds.length} cây sâm thành công!`, { variant: 'success' });
      // Tùy chọn: Reload lại dữ liệu nếu cần
      if (selectedLot?.loSam_ID) reloadLotDetail(selectedLot.loSam_ID);
    } else {
      enqueueSnackbar('Đính kèm file thất bại. Vui lòng thử lại!', { variant: 'error' });
    }
  }, [
    gridSize.x, 
    gridPositions, 
    deselectedGinsengs, 
    getGinsengInfo, 
    highlightStatus, 
    highlightHealth, 
    highlightAge, 
    addDinhKemFile, 
    enqueueSnackbar,
    selectedLot,
    reloadLotDetail
  ]);
  // ---> KẾT THÚC LOGIC IMPORT

  const renderGridContent = useMemo(() => {
    if (gardensLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!selectedGarden) {
      return (
        <>
         <Typography variant="h6" sx={{ mb: 2, px: { xs: 2, md: 0 } }}>
              Bản đồ lô sâm
            </Typography> 
         <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Vui lòng chọn vườn trồng để xem bản đồ lô sâm
          </Typography>
        </Box>
        </>
      );
    }
    if (!selectedLot && !lotName) {
      return (
        <>
         <Typography variant="h6" sx={{ mb: 2, px: { xs: 2, md: 0 } }}>
              Bản đồ lô sâm
            </Typography>
             <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            Vui lòng chọn lô sâm hoặc tạo lô mới
          </Typography>
        </Box>
        </>
      );
    }
    return (
      <LotGrid
        lotName={lotName}
        selectedLot={selectedLot}
        gridPositions={gridPositions}
        gridSize={gridSize}
        caySamsLoading={caySamsLoading}
        onCellClick={tracu.handlePositionClick}
        getGinsengInfo={getGinsengInfo}
        highlightStatus={highlightStatus}
        highlightHealth={highlightHealth}
        highlightAge={highlightAge}
        excludeLoggedThisMonth={false} // Không loại trừ cây đã cập nhật
        onDeselectGinseng={handleDeselectGinseng}
        deselectedGinsengs={deselectedGinsengs}
        onOpenDrawer={(caySamData, position) => {
          tracu.handlePositionClick(position.x, position.y);
        }}
        loaiTuoiOptions={tracu.loaiTuoiOptions}
        diemSucKhoeOptions={diemSucKhoeOptions}
        onUpdateCaySam={(caySamData) => {
          // Handle updating cay sam data
        }}
        onSaveCaySam={() => {
          // Reload data after save
          if (selectedLot?.loSam_ID) {
            reloadLotDetail(selectedLot.loSam_ID);
          }
        }}
        onDeleteCaySam={() => {
          if (selectedLot?.loSam_ID) {
            reloadLotDetail(selectedLot.loSam_ID);
          }
        }}
        onCaySamUpdated={handleCaySamUpdated}
      />
    );
  }, [
    gardensLoading, 
    selectedGarden, 
    selectedLot, 
    lotName, 
    gridPositions, 
    gridSize, 
    caySamsLoading, 
    tracu, 
    getGinsengInfo, 
    highlightStatus, 
    highlightHealth, 
    highlightAge, 
    handleDeselectGinseng, 
    deselectedGinsengs, 
    diemSucKhoeOptions, 
    reloadLotDetail,
    handleCaySamUpdated
  ]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}> 
      <CustomBreadcrumbs
        heading="Quản lý lô sâm"
        links={[
          { name: 'Dashboard', href: paths.dashboard.root },
          { name: 'Lô sâm' },
        ]}
      />
      
      <Grid container spacing={3}>
        {/* Bên trái: Cấu hình lô sâm */}
        <Grid xs={12} md={3}>
           <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Cấu hình lô sâm
            </Typography>
            
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Chọn vườn trồng</InputLabel>
                <Select
                  // Thay đổi: Sử dụng selectedGarden.value thay vì vuonTrong_ID
                  value={selectedGarden?.value || ''}
                  label="Chọn vườn trồng"
                  onChange={(e) => handleGardenChange(Number(e.target.value))}
                  disabled={gardensLoading}
                >
                  {optiongardens.map((garden) => (
                    <MenuItem key={garden.value || ''} value={garden.value || ''}>
                      {garden.text || ''}
                    </MenuItem>
                  ))} 
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Chọn lô sâm</InputLabel>
                <Select
                  value={selectedLot?.loSam_ID || ''}
                  label="Chọn lô sâm"
                  onChange={(e) => handleLotChange(Number(e.target.value))}
                  disabled={!selectedGarden || lotsLoading}
                >
                  {lotsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Đang tải...
                    </MenuItem>
                  ) : (
                    lots.map((lot) => (
                      <MenuItem key={lot.loSam_ID} value={lot.loSam_ID}>
                        {lot.tenLo} ({lot.soCot} x {lot.soHang})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Chiều rộng (X)"
                  type="number"
                  value={(() => { if (selectedLot) return selectedLot.soCot; if (gridSize.x > 0) return gridSize.x - 1; return 0; })()}
                  onChange={(e) => setGridSize((prev) => ({ ...prev, x: Number(e.target.value) + 1 }))}
                  disabled={!!selectedLot}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Chiều cao (Y)"
                  type="number"
                  value={gridSize.y}
                  onChange={(e) => setGridSize((prev) => ({ ...prev, y: Number(e.target.value) }))}
                  disabled={!!selectedLot}
                  sx={{ flex: 1 }}
                />
              </Box>

              {selectedPositions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Vị trí đã chọn: {selectedPositions.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPositions.map((pos) => `(${pos.x}, ${pos.y})`).join(', ')}
                  </Typography>
                </Box>
              )}

              {selectedLot && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Thông tin lô sâm
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Tổng số cây sâm: {caySams.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Diện tích: {selectedLot.dienTich} m²
                  </Typography>
                </Box>
              )}
            </Stack>
          </Card>

          {/* Camera Viewer - chỉ hiển thị khi đã load xong dữ liệu chi tiết */}
          {selectedLot && lotDetailLoaded && (
            <Card sx={{ p: 3,mt:2,mb:2 }}>
            <CameraViewer    
              loSamId={selectedLot.loSam_ID}
              vuongSamId={selectedLot.vuonTrong_ID} 
            />
            </Card>
          )} 
           <Card sx={{ p: 3,mt:3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                    <Iconify icon="solar:tag-bold" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Chú thích
                  </Typography>
              <ChuthichSection/>
            </Card> 
        </Grid> 
        
        {/* Bên phải: Filter + LotGrid + CaySamDrawer */}
        <Grid xs={12} md={9}>
          <Stack spacing={3}>
            {/* Filter ở phía trên */}
            <Card sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Bộ lọc cây sâm</Typography>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={handleOpenQRDialog}
                    startIcon={<Iconify icon="feather:download" />}
                  >
                    In QR
                  </Button>
                <InMaQRPopup
                    open={qrPopupOpen}
                    onClose={handleCloseQRDialog}
                    gridPositions={gridPositions}
                    gridSize={gridSize}
                    labelLot={lotName}
                  />
              </Stack>
              {/* <--- 6. TRUYỀN PROPS VÀO FILTER ---> */}
              <FilterCaySam
                isSelect = {lotDetailLoaded} 
                onApply={handleApplyFilter}
                filteredCount={filteredCount}
                onUpdateGrowth={handleUpdateGrowth}
                onClearFilter={() => { 
                  setHighlightStatus(0);
                  setHighlightHealth(0);
                  setHighlightAge(0);
                  setDeselectedGinsengs(new Set());
                  setFilteredCount(0);
                }}
                onImportFile={handleImportFilePdf} // Prop xử lý file
                loadingImport={loadingAddFile}     // Prop loading
              />
            </Card>

            {/* LotGrid ở giữa */}
          <Card sx={{ p: { xs: 0, md: 3 } }}>
              {renderGridContent}
          </Card> 

            {/* CaySamDrawer ở phía dưới */}
            {tracu.openDrawer && (
              <Card sx={{ p: 3 }}>
                <CaySamDrawer
                  open={tracu.openDrawer}
                  onClose={handleCloseDrawer}
                  title={tracu.selectedCellInfo?.isEdit ? 'Cập nhật cây sâm' : 'Thêm cây sâm mới'}
                  lotName={selectedLot?.tenLo || ''}
                  lotCode={selectedLot?.maLo || ''}
                  // Thay đổi: Hiển thị tên vườn từ text của OptionModel
                  gardenName={selectedGarden?.text || ''}
                  positionLabel={tracu.selectedCellInfo?.position || ''}
                  caySamData={tracu.selectedCellInfo?.caySam || null}
                  onUpdateCaySam={(updatedData) => {
                    // Cập nhật caySamData trong selectedCellInfo
                    if (tracu.selectedCellInfo?.caySam) {
                      tracu.setSelectedCellInfo({
                        ...tracu.selectedCellInfo,
                        caySam: { ...tracu.selectedCellInfo.caySam, ...updatedData }
                      });
                    }
                  }}
                  loaiTuoiOptions={tracu.loaiTuoiOptions}  
                  diemSucKhoeOptions={diemSucKhoeOptions}
                  loading={tracu.loading}
                  onRefresh={handleCaySamUpdated}
                /> 
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
      
      {/* Update Growth Modal */}
      <UpdateCaySams
        open={updateGrowthOpen}
        onClose={() => setUpdateGrowthOpen(false)}
        loading={false}
        items={filteredCaySams}    
        caySamById={(() => {
          const allow = new Set<string | number>(filteredCaySams.map((nk) => nk.caySamId || nk.id));
          const map: Record<string | number, any> = {};
          gridPositions.forEach((row) =>
            row.forEach((pos) => {
              const cs = pos.caySam;
              if (cs && allow.has(cs.caySam_ID)) map[cs.caySam_ID] = cs;  
            })
          );
          return map;
        })()}
        filterState={{
          tinhTrang: highlightStatus || undefined, 
          diem: highlightHealth || undefined,
        }}
        onConfirm={async (data) => { 
          console.log('Update growth confirmed:', data);
          setUpdateGrowthOpen(false);
          
          // Reload lot detail và cập nhật grid positions
          if (selectedLot) {
            await reloadLotDetail(selectedLot.loSam_ID || 0);
          }
          
          // Reset filter state 
          setHighlightStatus(0);
          setHighlightHealth(0);
          setHighlightAge(0);
          setDeselectedGinsengs(new Set());
          setFilteredCount(0);
        }}
      />
    </Container>
  );
}